class ConstructorProductRecommendations extends HTMLElement {
    // ─── CONFIG ────────────────────────────────────────────────────────────
    static API_KEY = window.CONSTRUCTOR_API_KEY;
    static NUM_RESULTS = 10;
    // ───────────────────────────────────────────────────────────────────────

    connectedCallback() {
        // Cache card template once
        const tpl = this.querySelector('#constructor-product-card');
        if (tpl) {
            const div = document.createElement('div');
            div.appendChild(tpl.content.cloneNode(true));
            this.cardTemplate = div.innerHTML.trim();
        } else {
            this.cardTemplate = null;
        }

        const handleIntersection = (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            this.fetchRecommendations();
        };

        new IntersectionObserver(handleIntersection.bind(this), {
            rootMargin: '0px 0px 400px 0px'
        }).observe(this);
    }

    // ── Fetch ───────────────────────────────────────────────────────────────
    async fetchRecommendations() {
        const podId = this.dataset.podId;
        const itemId = this.dataset.itemId;
        const variationId = this.dataset.variationId;

        if (!podId || !itemId) {
            console.warn('[ConstructorProductRecommendations] Missing pod-id or item-id.');
            return;
        }

        const params = new URLSearchParams({
            key: ConstructorProductRecommendations.API_KEY,
            item_id: itemId,
            num_results: ConstructorProductRecommendations.NUM_RESULTS
        });

        if (variationId) params.set('variation_id', variationId);

        try {
            const res = await fetch(
                `https://ac.cnstrc.com/recommendations/v1/pods/${encodeURIComponent(podId)}?${params}`
            );
            if (!res.ok) throw new Error(`Constructor recommendations error: ${res.status}`);
            const data = await res.json();
            this.render(data);
        } catch (err) {
            console.error('[ConstructorProductRecommendations]', err);
        }
    }

    // ── Render ───────────────────────────────────────────────────────────────
    render(data) {
        const results = data.response?.results ?? [];
        const resultId = data.result_id ?? '';
        const podId = data.response?.pod?.id ?? this.dataset.podId;
        const numResults = data.total_num_results ?? results.length;

        if (!results.length) return;

        // Seed items — comma-separated item_ids used in the request
        const seedItems = this.dataset.itemId ?? '';

        // ── Recommendations container (tracking wrapper) ──────────────────────
        const wrapper = this.querySelector('.constructor-recommendations__wrapper');
        if (!wrapper) {
            console.warn(
                '[ConstructorProductRecommendations] Missing .constructor-recommendations__wrapper'
            );
            return;
        }

        // Set tracking attributes on the wrapper
        wrapper.dataset.cnstrcRecommendations = '';
        wrapper.dataset.cnstrcRecommendationsPodId = podId;
        wrapper.dataset.cnstrcResultId = resultId;
        wrapper.dataset.cnstrcNumResults = numResults;
        wrapper.dataset.cnstrcRecommendationsSeedItems = seedItems;

        // ── Slides ────────────────────────────────────────────────────────────
        const slidesList = this.querySelector('.glide__slides');
        if (!slidesList) {
            console.warn('[ConstructorProductRecommendations] Missing .glide__slides');
            return;
        }

        slidesList.innerHTML = '';

        results.forEach((item) => {
            slidesList.appendChild(this.renderCard(item));
        });

        // Show the section now that slides are populated
        this.classList.add('constructor-product-recommendations--loaded');

        // ── Trigger GlideComponent init (with delay to allow DOM rendering) ───
        // Small delay ensures .glide__slides elements have their width calculated
        // before Glide reinitializes, preventing width: 0px issue
        setTimeout(() => {
            publish(PUB_SUB_EVENTS.glideUpdate, { id: this.dataset.sectionId });
        }, 100);
    }

    // ── Card ─────────────────────────────────────────────────────────────────
    renderCard(item) {
        const data = item.data ?? {};
        const name = item.value ?? data.name ?? '';
        const handle = data.handle ?? '';
        const url = data.url ?? '#';
        const imageUrl = data.image_url ?? '';
        const price = data.price != null ? `$${Number(data.price).toFixed(2)}` : '';
        const variationId = data.variation_id ?? '';
        const strategyId = item.strategy?.id ?? '';

        let li;

        if (this.cardTemplate) {
            const html = this.cardTemplate
                .replaceAll('#PRODUCT_NAME#', this.escapeHtml(name))
                .replaceAll('#HANDLE#', this.escapeAttr(handle))
                .replaceAll('#URL#', this.escapeAttr(url))
                .replaceAll('#IMAGE_URL#', this.escapeAttr(imageUrl ? imageUrl + '&width=302' : ''))
                .replaceAll('#IMAGE_ALT#', this.escapeAttr(name))
                .replaceAll('#PRICE#', this.escapeHtml(price));

            const wrapper = document.createElement('div');
            wrapper.innerHTML = html.trim();
            li = wrapper.firstElementChild;
        } else {
            // Minimal fallback if template is missing
            li = document.createElement('li');
            li.className = 'slideshow__slide glide__slide';
            li.innerHTML = `
        <a href="${this.escapeAttr(url)}">
          ${imageUrl ? `<img src="${this.escapeAttr(imageUrl)}" alt="${this.escapeAttr(name)}" loading="lazy" />` : ''}
          <div>${this.escapeHtml(name)}</div>
          ${price ? `<div>${this.escapeHtml(price)}</div>` : ''}
        </a>
      `;
        }

        // ── Tracking attributes on the card ────────────────────────────────────
        li.dataset.cnstrcItem = 'recommendation';
        li.dataset.cnstrcSection = 'Products';
        li.dataset.cnstrcItemId = handle;
        li.dataset.cnstrcItemName = name;
        li.dataset.cnstrcStrategyId = strategyId;
        if (variationId) li.dataset.cnstrcItemVariationId = variationId;
        if (data.price != null) li.dataset.cnstrcItemPrice = Number(data.price).toFixed(2);

        return li;
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    escapeHtml(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    escapeAttr(str) {
        return String(str).replace(/"/g, '&quot;');
    }
}

customElements.define('constructor-product-recommendations', ConstructorProductRecommendations);
